import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navigation />

      <main className="flex-grow">
        {/* Header */}
        <div className="bg-black text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

          <p className="text-gray-700 mb-4">
            At Crash Champions, accessible from{' '}
            <a href="https://crashchampions.com/" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">
              https://crashchampions.com/
            </a>, one of our main priorities is the privacy of our visitors. This Privacy Policy document contains
            types of information that is collected and recorded by Crash Champions and how we use it.
          </p>
          <p className="text-gray-700 mb-4">
            If you have additional questions or require more information about our Privacy Policy, do not hesitate to
            contact us.
          </p>
          <p className="text-gray-700 mb-8">
            This Privacy Policy applies only to our online activities and is valid for visitors to our website with
            regards to the information that they shared and/or collect in Crash Champions. This policy is not
            applicable to any information collected offline or via channels other than this website.
          </p>

          {/* Consent */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Consent</h2>
            <p className="text-gray-700">
              By using our website, you hereby consent to our Privacy Policy and agree to its terms.
            </p>
          </section>

          {/* Information we collect */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
            <p className="text-gray-700 mb-3">
              The personal information that you are asked to provide, and the reasons why you are asked to provide it,
              will be made clear to you at the point we ask you to provide your personal information.
            </p>
            <p className="text-gray-700 mb-3">
              If you contact us directly, we may receive additional information about you such as your name, email
              address, phone number, the contents of the message and/or attachments you may send us, and any other
              information you may choose to provide.
            </p>
            <p className="text-gray-700">
              When you register for an Account, we may ask for your contact information, including items such as name,
              company name, address, email address, and telephone number.
            </p>
          </section>

          {/* How we use your information */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
            <p className="text-gray-700 mb-3">We use the information we collect in various ways, including to:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Provide, operate, and maintain our website</li>
              <li>Improve, personalize, and expand our website</li>
              <li>Understand and analyze how you use our website</li>
              <li>Develop new products, services, features, and functionality</li>
              <li>
                Communicate with you, either directly or through one of our partners, including for customer service, to
                provide you with updates and other information relating to the website, and for marketing and promotional
                purposes
              </li>
              <li>Send you emails</li>
              <li>Find and prevent fraud</li>
            </ul>
          </section>

          {/* Log Files */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Log Files</h2>
            <p className="text-gray-700">
              Crash Champions follows a standard procedure of using log files. These files log visitors when they visit
              websites. All hosting companies do this and a part of hosting services' analytics. The information
              collected by log files include internet protocol (IP) addresses, browser type, Internet Service Provider
              (ISP), date and time stamp, referring/exit pages, and possibly the number of clicks. These are not linked
              to any information that is personally identifiable. The purpose of the information is for analyzing
              trends, administering the site, tracking users' movement on the website, and gathering demographic
              information.
            </p>
          </section>

          {/* Cookies and Web Beacons */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookies and Web Beacons</h2>
            <p className="text-gray-700">
              Like any other website, Crash Champions uses 'cookies'. These cookies are used to store information
              including visitors' preferences, and the pages on the website that the visitor accessed or visited. The
              information is used to optimize the users' experience by customizing our web page content based on
              visitors' browser type and/or other information.
            </p>
          </section>

          {/* Advertising Partners */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Advertising Partners Privacy Policies</h2>
            <p className="text-gray-700 mb-3">
              You may consult this list to find the Privacy Policy for each of the advertising partners of Crash
              Champions.
            </p>
            <p className="text-gray-700 mb-3">
              Third-party ad servers or ad networks use technologies like cookies, JavaScript, or Web Beacons that are
              used in their respective advertisements and links that appear on Crash Champions, which are sent directly
              to users' browser. They automatically receive your IP address when this occurs. These technologies are
              used to measure the effectiveness of their advertising campaigns and/or to personalize the advertising
              content that you see on websites that you visit.
            </p>
            <p className="text-gray-700">
              Note that Crash Champions has no access to or control over these cookies that are used by third-party
              advertisers.
            </p>
          </section>

          {/* Microsoft Clarity */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Microsoft Clarity</h2>
            <p className="text-gray-700">
              We partner with Microsoft Clarity and Microsoft Advertising to capture how you use and interact with our
              website through behavioral metrics, heatmaps, and session replay to improve and market our
              products/services. Website usage data is captured using first and third-party cookies and other tracking
              technologies to determine the popularity of products/services and online activity. Additionally, we use
              this information for site optimization, fraud/security purposes, and advertising. For more information
              about how Microsoft collects and uses your data, visit the Microsoft Privacy Statement.
            </p>
          </section>

          {/* Third Party Privacy Policies */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Third Party Privacy Policies</h2>
            <p className="text-gray-700">
              Crash Champions's Privacy Policy does not apply to other advertisers or websites. Thus, we are advising
              you to consult the respective Privacy Policies of these third-party ad servers for more detailed
              information. It may include their practices and instructions about how to opt-out of certain options. You
              can choose to disable cookies through your individual browser options. To know more detailed information
              about cookie management with specific web browsers, it can be found at the browsers' respective websites.
            </p>
          </section>

          {/* California Privacy Policy */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">California Privacy Policy</h2>
            <p className="text-gray-700 mb-3">
              This California Privacy Policy is intended to comply with the California Consumer Privacy Act (CCPA), the
              California Online Privacy Protection Act (CalOPPA), and other applicable privacy laws. Consumers with
              disabilities may access this policy in an alternative format by sending an email to:{' '}
              <a href="mailto:CaliforniaConsumerInformationRequest@crashchampions.com" className="text-red-600 hover:underline">
                CaliforniaConsumerInformationRequest@crashchampions.com
              </a>.
            </p>
            <p className="text-gray-700 mb-4">
              We do not and will not sell Personal Information. Additionally, we will not share this information with
              third parties who sell or use the information for their own purposes unless you direct us to do so.
            </p>
            <p className="text-gray-700 mb-4">
              For purposes of this California Privacy Policy, "Personal Information" means anything that identifies,
              relates to, describes, is reasonably capable of being associated with, or could be reasonably linked,
              directly or indirectly, with a particular California consumer or household.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-6">
              Notice of Categories of Personal Information We Collect and How We Use It
            </h3>
            <ul className="list-disc list-inside text-gray-700 space-y-4">
              <li>
                <strong>Personal contact information</strong>, such as name, address, telephone number and email
                address. We obtain this information from: (1) you when you communicate with us through our website,
                social media platforms, surveys and forms; (2) publicly available databases; (3) joint marketing
                partners and event sponsors, when they share the information with us; and (4) referral sources. We use
                and share your personal contact information with trusted service providers to promote and provide our
                products and services to you, to communicate with you and, for job applicants and employees, to manage
                our human resources activities.
              </li>
              <li>
                <strong>Information necessary to provide our products and services to you</strong>, such as vehicle
                information, insurance information, order information, payment information, and credit information. We
                obtain this information from you and use and share it with trusted service providers to provide our
                products and services to you and to manage our relationship with you.
              </li>
              <li>
                <strong>Commercial information</strong>, including products or services purchased, obtained or
                considered, and other purchase histories or tendencies. We obtain this information from you and use and
                share it with trusted service providers to promote and provide our products and services to you and to
                manage our relationship with you.
              </li>
              <li>
                <strong>Internet and other electronic network and app activity information</strong>, such as browsing
                history, search history, IP address, and information regarding your interaction with an internet website,
                application or advertisement. We obtain this information from you and through the use of cookies. We use
                and share this information with trusted service providers to promote and provide our products and
                services to you and to manage our relationship with you.
              </li>
              <li>
                <strong>Professional, employment-related, and educational information</strong>. We obtain this
                information from you, employment recruiters and referral sources when you apply for a job with us or
                become employed by us. We use and share this information with trusted service providers to manage our
                human resources activities.
              </li>
            </ul>
          </section>

          {/* Right to Know */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Right to Know About Personal Information Collected and Disclosed
            </h2>
            <p className="text-gray-700 mb-3">
              You have the right to know about the Personal Information we collected and disclosed about you during the
              prior 12 months.
            </p>
            <p className="text-gray-700">
              You can obtain the Personal Information we collect and disclose by submitting a verifiable written request
              to us or by calling us at our toll-free telephone number: <strong>866-730-5464</strong>, or by asking for and
              completing a written request form at one of our physical locations. To ensure that the request is coming
              from you and to protect the security of your Personal Information.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-black text-white rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Questions About Your Privacy?</h2>
            <p className="text-gray-300 mb-2">Crash Champions Collision Repair Centers</p>
            <p className="text-gray-300 mb-2">601 Oakmont Lane, Westmont, IL 60559</p>
            <p className="text-gray-300 mb-4">T: (877) 846-9500</p>
            <a
              href="https://www.crashchampions.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-red-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
            >
              www.crashchampions.com
            </a>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
